import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { downloadFile } from "@/components/InvoiceDialogs";
import { Download, Mail } from "lucide-react";

const money = (v) => (v || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ST_PL = { issued: "wystawiona", paid: "opłacona", cancelled: "anulowana" };
const thisMonth = () => new Date().toLocaleDateString("sv-SE").slice(0, 7);
const CARDS = [["gross", "Zafakturowano brutto"], ["vat_pln", "VAT w PLN"], ["paid", "Opłacone"], ["unpaid", "Nieopłacone"], ["overdue", "Po terminie"], ["corrections", "Korekty"]];

function CurrencyCard({ cur, v }) {
  return (
    <div className="border border-border bg-white rounded-md p-4" data-testid={`report-currency-${cur}`}>
      <p className="font-[Outfit] font-semibold text-[#193962] mb-2">{cur} <span className="text-xs text-muted-foreground font-normal">· {v.count} dok. · netto {money(v.net)} · VAT {money(v.vat)}</span></p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {CARDS.map(([k, l]) => (
          <div key={k} data-testid={`report-${cur}-${k}`}>
            <p className="text-[11px] text-muted-foreground">{l}</p>
            <p className={`tabular-nums font-medium ${k === "overdue" && v[k] > 0 ? "text-red-600" : ""}`}>{money(v[k])} {k === "vat_pln" ? "PLN" : cur}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export const SalesReport = () => {
  const [month, setMonth] = useState(thisMonth());
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/reports/sales", { params: { month } }).then((r) => setRep(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Błąd"));
  useEffect(() => { if (month) load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    setBusy(true);
    try { const r = await api.post("/reports/sales/send", { month }); toast.success(`Wysłano raport do ${r.data.to}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Błąd"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="sales-report">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-white" data-testid="report-month" />
        <span className="text-xs text-muted-foreground">wg daty wystawienia · automatycznie do księgowej 1. dnia miesiąca</span>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => downloadFile(`/reports/sales.csv?month=${month}`, `raport-sprzedazy-${month}.csv`)} data-testid="report-csv"><Download className="h-4 w-4 mr-1.5" />CSV</Button>
          <Button variant="outline" onClick={() => downloadFile(`/reports/sales.pdf?month=${month}`, `raport-sprzedazy-${month}.pdf`)} data-testid="report-pdf"><Download className="h-4 w-4 mr-1.5" />PDF</Button>
          <Button onClick={send} disabled={busy} className="bg-[#193962] text-white" data-testid="report-send"><Mail className="h-4 w-4 mr-1.5" />{busy ? "Wysyłanie…" : "Wyślij do księgowej"}</Button>
        </div>
      </div>
      {rep && (<>
        {Object.keys(rep.by_currency).length === 0 && <p className="text-sm text-muted-foreground" data-testid="report-empty">Brak faktur wystawionych w {rep.month}.</p>}
        <div className="grid md:grid-cols-2 gap-3">{Object.entries(rep.by_currency).map(([c, v]) => <CurrencyCard key={c} cur={c} v={v} />)}</div>
        <p className="text-xs text-muted-foreground" data-testid="report-proformas">Proformy w miesiącu (informacyjnie): {rep.proformas.count}</p>
        {rep.rows.length > 0 && (
          <div className="border border-border bg-white rounded-md divide-y divide-border text-sm" data-testid="report-rows">
            {rep.rows.map((r) => (
              <div key={r.number} className="flex flex-wrap gap-2 px-4 py-2">
                <span className="font-medium">{r.number}</span><span className="text-zinc-500 truncate flex-1 min-w-0">{r.buyer}{r.corrects && ` · korekta do ${r.corrects}`}</span>
                <span className="tabular-nums">{money(r.gross)} {r.currency}</span><span className="text-[11px] text-zinc-500">{ST_PL[r.status] || r.status}</span>
              </div>
            ))}
          </div>
        )}
        {rep.sent?.length > 0 && <p className="text-[11px] text-muted-foreground" data-testid="report-sent-log">Wysłano: {rep.sent.map((s) => `${s.to} (${new Date(s.at).toLocaleString("pl-PL")}${s.by === "auto-run" ? ", auto" : ""})`).join(" · ")}</p>}
      </>)}
    </div>
  );
};
